// Accepts a subset of hom-api's storeEventsPayloadSchema: stricter drops an event, looser stalls the whole batch.

type FieldCheck = [field: string, isInvalid: (value: unknown) => boolean]

const TO_ISO_STRING_PATTERN = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d{1,3})?Z$/

const isMissingString = (value: unknown): boolean => typeof value !== 'string' || value === ''

const isInvalidIsoDate = (value: unknown): boolean =>
  typeof value !== 'string' || !TO_ISO_STRING_PATTERN.test(value) || Number.isNaN(Date.parse(value))

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isInvalidOptionalSafeInteger = (value: unknown): boolean =>
  value !== undefined && !(typeof value === 'number' && Number.isSafeInteger(value))

const isInvalidOptionalObject = (value: unknown): boolean => value !== undefined && !isPlainObject(value)

const isPlayedRange = (range: unknown): boolean =>
  Array.isArray(range) &&
  range.length === 2 &&
  range.every((bound) => typeof bound === 'number' && Number.isSafeInteger(bound))

const isInvalidOptionalVideoExtra = (value: unknown): boolean =>
  value !== undefined &&
  !(isPlainObject(value) && Array.isArray(value.playedRanges) && value.playedRanges.every(isPlayedRange))

const EVENT_FIELD_CHECKS: FieldCheck[] = [
  ['id', isMissingString],
  ['time', isInvalidIsoDate],
  ['viewKey', isMissingString],
  ['eventName', isMissingString],
  ['viewportId', isMissingString],
  ['extra', isInvalidOptionalObject],
]

const VIDEO_EVENT_FIELD_CHECKS: FieldCheck[] = [
  ['id', isMissingString],
  ['createdAt', isInvalidIsoDate],
  ['videoId', isMissingString],
  ['videoTime', isInvalidOptionalSafeInteger],
  ['viewKey', isMissingString],
  ['eventName', isMissingString],
  ['viewportId', isMissingString],
  ['extra', isInvalidOptionalVideoExtra],
]

// The API sees the JSON form: Infinity and NaN become null, undefined fields disappear.
function toJsonValue(value: unknown): unknown {
  try {
    const json = JSON.stringify(value)
    return json === undefined ? undefined : JSON.parse(json)
  } catch {
    return undefined
  }
}

function getInvalidReason(value: unknown, fieldChecks: FieldCheck[]): string | null {
  const json = toJsonValue(value)

  if (!isPlainObject(json)) {
    return json === null ? 'null' : Array.isArray(json) ? 'array' : typeof json
  }

  const allowedFields = fieldChecks.map(([field]) => field)
  const hasUnknownFields = Object.keys(json).some((field) => !allowedFields.includes(field))
  const invalidFields = [
    ...fieldChecks.filter(([field, isInvalid]) => isInvalid(json[field])).map(([field]) => field),
    ...(hasUnknownFields ? ['unknown-fields'] : []),
  ]

  return invalidFields.length > 0 ? `invalid:${invalidFields.join(',')}` : null
}

export function getInvalidEventReason(value: unknown): string | null {
  return getInvalidReason(value, EVENT_FIELD_CHECKS)
}

export function getInvalidVideoEventReason(value: unknown): string | null {
  return getInvalidReason(value, VIDEO_EVENT_FIELD_CHECKS)
}
